import React, { useState, useMemo } from 'react';
import {
  X, Check, Search,
  Key, Laptop, Globe, Layers, Database, Users, Settings, Shield, ShieldCheck, ShieldAlert,
  CheckCircle, Activity, Package, Lock, UserCheck, Server, GitBranch, Grid, EyeOff, Tag,
  Archive, Box, LogIn, Code, FileText, Smartphone
} from 'lucide-react';
import { SAMPLE_PRODUCTS, PRODUCT_CATEGORIES } from './PillarTemplate';
import './TechnologySelectorModal.css';

/**
 * Icon mapping for categories
 */
const ICON_MAP = {
  key: Key,
  laptop: Laptop,
  globe: Globe,
  layers: Layers,
  database: Database,
  users: Users,
  settings: Settings,
  shield: Shield,
  'shield-check': ShieldCheck,
  'shield-alert': ShieldAlert,
  'check-circle': CheckCircle,
  activity: Activity,
  package: Package,
  lock: Lock,
  'user-check': UserCheck,
  server: Server,
  'git-branch': GitBranch,
  grid: Grid,
  'eye-off': EyeOff,
  tag: Tag,
  search: Search,
  archive: Archive,
  box: Box,
  'log-in': LogIn,
  code: Code,
  'file-text': FileText,
  smartphone: Smartphone
};

/**
 * TechnologySelectorModal Component
 *
 * Modal for selecting a technology to assign to a node.
 * Shows products from the same category and allows filtering.
 */
function TechnologySelectorModal({
  isOpen,
  onClose,
  node,
  pillarCode,
  onSelectTechnology
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Get available products for this pillar
  const allProducts = useMemo(() => {
    return SAMPLE_PRODUCTS[pillarCode] || [];
  }, [pillarCode]);

  // Get categories for this pillar
  const categories = useMemo(() => {
    return PRODUCT_CATEGORIES[pillarCode] || [];
  }, [pillarCode]);

  // Filter products based on search and category
  const filteredProducts = useMemo(() => {
    let products = allProducts;

    // If node has a category, prioritize those products
    const nodeCategory = node?.category;
    if (nodeCategory && !selectedCategory) {
      // Show category products first, then others
      const categoryProducts = products.filter(p => p.category === nodeCategory);
      const otherProducts = products.filter(p => p.category !== nodeCategory);
      products = [...categoryProducts, ...otherProducts];
    }

    // Filter by selected category
    if (selectedCategory) {
      products = products.filter(p => p.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      products = products.filter(p =>
        p.name.toLowerCase().includes(term) ||
        p.product.toLowerCase().includes(term)
      );
    }

    return products;
  }, [allProducts, searchTerm, selectedCategory, node?.category]);

  // Handle product selection
  const handleSelect = (product) => {
    onSelectTechnology({
      label: product.product,
      vendorName: product.name,
      productName: product.product,
      category: product.category,
      icon: getCategoryIcon(product.category)
    });
    onClose();
  };

  // Get icon for a category
  const getCategoryIcon = (category) => {
    const cat = categories.find(c => c.key === category);
    return cat?.icon || 'layers';
  };

  // Get icon component
  const getIconComponent = (iconName) => {
    return ICON_MAP[iconName] || Layers;
  };

  if (!isOpen) return null;

  return (
    <div className="tsm-overlay" onClick={onClose}>
      <div className="tsm-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="tsm-header">
          <div className="tsm-header-content">
            <h3>Select Technology</h3>
            <p className="tsm-subtitle">
              {node?.label ? `Replace "${node.label}"` : 'Choose a technology for this node'}
            </p>
          </div>
          <button className="tsm-close-btn" onClick={onClose} title="Close">
            <X size={20} />
          </button>
        </div>

        {/* Search and filters */}
        <div className="tsm-filters">
          <div className="tsm-search">
            <Search size={16} className="tsm-search-icon" />
            <input
              type="text"
              className="tsm-search-input"
              placeholder="Search technologies..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>

          <div className="tsm-category-filters">
            <button
              className={`tsm-category-btn ${!selectedCategory ? 'tsm-category-btn--active' : ''}`}
              onClick={() => setSelectedCategory(null)}
            >
              All
            </button>
            {categories.map(cat => {
              const IconComp = getIconComponent(cat.icon);
              return (
                <button
                  key={cat.key}
                  className={`tsm-category-btn ${selectedCategory === cat.key ? 'tsm-category-btn--active' : ''}`}
                  onClick={() => setSelectedCategory(cat.key)}
                  title={cat.label}
                >
                  <IconComp size={14} />
                  <span>{cat.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Product list */}
        <div className="tsm-products">
          {filteredProducts.length === 0 ? (
            <div className="tsm-empty">
              <p>No technologies found matching your criteria.</p>
            </div>
          ) : (
            filteredProducts.map((product, index) => {
              const IconComp = getIconComponent(getCategoryIcon(product.category));
              const isCurrentSelection = node?.productName === product.product;

              return (
                <button
                  key={`${product.name}-${product.product}-${index}`}
                  className={`tsm-product ${isCurrentSelection ? 'tsm-product--selected' : ''}`}
                  onClick={() => handleSelect(product)}
                >
                  <div className="tsm-product-icon">
                    <IconComp size={20} />
                  </div>
                  <div className="tsm-product-info">
                    <div className="tsm-product-name">{product.product}</div>
                    <div className="tsm-product-vendor">{product.name}</div>
                  </div>
                  <div className="tsm-product-category">
                    {categories.find(c => c.key === product.category)?.label || product.category}
                  </div>
                  {isCurrentSelection && (
                    <div className="tsm-product-check">
                      <Check size={16} />
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="tsm-footer">
          <button className="tsm-cancel-btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default TechnologySelectorModal;
